// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2026 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { Pill } from "@recidiviz/design-system";
import { rem } from "polished";
import styled from "styled-components";

import { palette } from "~design-system";

export const BadgePillStyled = styled(Pill)<{
  $borderColor: string;
}>`
  border-radius: ${rem(4)};
  border: 1px solid ${(props) => props.$borderColor};
  font-size: ${rem(12)};
  font-weight: 600;
  height: ${rem(20)};
  padding: 0 ${rem(6)};
  vertical-align: text-top;
`;

export const statusStyles = {
  SLATE: {
    backgroundColor: palette.slate05,
    borderColor: palette.slate20,
    color: palette.slate80,
  },
  // Solid dark-grey/white variant, for callers that want a plain, low-
  // emphasis tag rather than SLATE's light outline style.
  SLATE_DARK: {
    backgroundColor: palette.slate70,
    borderColor: palette.slate70,
    color: palette.white,
  },
  PURPLE: {
    backgroundColor: "rgb(242,240,245)",
    borderColor: "rgb(137,115,165)",
    color: "rgb(98,68,136)",
  },
  BLUE: {
    backgroundColor: "rgb(239,243,255)",
    borderColor: "rgb(162,179,239)",
    color: "rgb(0,56,124)",
  },
  YELLOW: {
    color: "rgb(168,44,0)",
    backgroundColor: "rgb(255,248,222)",
    borderColor: "rgb(252,213,121)",
  },
  GREEN: {
    backgroundColor: "rgb(239,255,229)",
    borderColor: "rgb(166,235,132)",
    color: "rgb(0,105,8)",
  },
  RED: {
    backgroundColor: "rgb(255,244,249)",
    borderColor: "rgb(255,204,223)",
    color: "rgb(179,9,60)",
  },
  ORANGE: {
    backgroundColor: "rgb(255, 234, 208)",
    borderColor: "rgb(250, 187, 135)",
    color: "rgb(152, 61, 1)",
  },
  TEAL: {
    backgroundColor: "rgb(238, 247, 248)",
    borderColor: "transparent",
    color: "rgb(0, 108, 103)",
  },
  GOLD: {
    backgroundColor: "rgb(255, 249, 215)",
    borderColor: "transparent",
    color: "rgb(159, 115, 45)",
  },
  BROWN: {
    backgroundColor: "rgba(161, 129, 0, 0.6)",
    borderColor: "transparent",
    color: "rgb(64, 54, 40)",
  },
} as const;

export type PaletteKey = keyof typeof statusStyles;

export function WorkflowsBadgePill({
  text,
  palette,
}: {
  text: string;
  palette: PaletteKey;
}) {
  const { color, borderColor, backgroundColor } = statusStyles[palette];
  return (
    <BadgePillStyled
      filled={true}
      color={backgroundColor}
      textColor={color}
      $borderColor={borderColor}
    >
      {text}
    </BadgePillStyled>
  );
}
